import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Truck, Snowflake, ChevronRight, Share2, Check } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const EQUIPMENT = [
  { id: "dry-van",  label: "Dry Van",  Icon: Truck,     leaseRPM: 3.00, ooRPM: 3.00, companyCPM: 0.80 },
  { id: "reefer",   label: "Reefer",   Icon: Snowflake, leaseRPM: 3.20, ooRPM: 3.20, companyCPM: 0.83 },
  { id: "flatbed",  label: "Flatbed",  Icon: Truck,     leaseRPM: 3.40, ooRPM: 3.40, companyCPM: 0.86 },
];

const OPERATIONS = [
  {
    id: "company",
    label: "Company Driver",
    sub: "85–90¢ CPM",
    calc: (miles, _rpm, cpm = 0.85) => {
      const gross = miles * cpm;
      return {
        gross,
        expenses: [{ label: "Company covers all truck expenses", amount: 0 }],
        net: gross,
        settlement: "Weekly direct deposit",
        note: `${(cpm * 100).toFixed(0)}¢ CPM — Zero out-of-pocket`,
      };
    },
  },
  {
    id: "lease",
    label: "Lease / Rent",
    sub: "88% Revenue",
    calc: (miles, rpm) => {
      const gross = miles * rpm * 0.88;
      const fuel = (miles / 7) * 6;          // $6/gal, 7 mpg
      const maintenance = miles * 0.10;      // $0.10/mi optional
      const expenses = [
        { label: `Fuel (÷7 mpg × $6/gal)`,      amount: fuel },
        { label: "Truck note (avg $600–800/wk)", amount: 700 },
        { label: "Trailer lease",                amount: 300 },
        { label: "Cargo insurance",              amount: 250 },
        { label: "Physical damage ins.",         amount: 250 },
        { label: "ELD",                          amount: 115 },
        { label: "Escrow ($200 × 10 wks)",       amount: 200 },
        { label: "Maintenance ($0.10/mi)",       amount: maintenance },
        { label: "Direct deposit + roadside",    amount: 65 },
      ];
      const totalExp = fuel + 700 + 300 + 250 + 250 + 115 + 200 + maintenance + 65;
      const net = gross - totalExp;
      return { gross, expenses, net, settlement: "Weekly settlement", note: `88% of $${rpm.toFixed(2)}/mi avg gross` };
    },
  },
  {
    id: "owner-op",
    label: "Owner Operator",
    sub: "Full Rate",
    calc: (miles, rpm) => {
      const gross = miles * rpm;
      const fuel = (miles / 7) * 6;
      const maint = miles * 0.12;
      const insurance = miles * 0.10;
      const expenses = [
        { label: `Fuel (÷7 mpg × $6/gal)`, amount: fuel },
        { label: "Maintenance est.",        amount: maint },
        { label: "Insurance est.",          amount: insurance },
      ];
      const net = gross - fuel - maint - insurance;
      return { gross, expenses, net, settlement: "Weekly settlement", note: `$${rpm.toFixed(2)}/mi avg base rate` };
    },
  },
];

function useAnimatedNumber(target) {
  const [current, setCurrent] = useState(target);
  const rafRef = useRef(null);
  useEffect(() => {
    const from = current;
    const start = performance.now();
    const dur = 420;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 2.5);
      setCurrent(from + (target - from) * eased);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return current;
}

function AnimNum({ value, prefix = "", suffix = "" }) {
  const n = useAnimatedNumber(value);
  return <span>{prefix}{Math.round(n).toLocaleString()}{suffix}</span>;
}

const VALID_EQ = EQUIPMENT.map((e) => e.id);
const VALID_OP = OPERATIONS.map((o) => o.id);

export default function GrossPayCalculator() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionRef = useRef(null);
  const [copied, setCopied] = useState(false);

  // Initialise from URL — fallback to defaults if param is invalid
  const initEq = VALID_EQ.includes(searchParams.get("eq")) ? searchParams.get("eq") : "dry-van";
  const initOp = VALID_OP.includes(searchParams.get("op")) ? searchParams.get("op") : "company";
  const rawMi = parseInt(searchParams.get("mi"), 10);
  const initMi = !isNaN(rawMi) ? Math.min(5000, Math.max(500, rawMi)) : 2500;

  const [equipment, setEquipment] = useState(initEq);
  const [operation, setOperation] = useState(initOp);
  const [miles, setMiles] = useState(initMi);

  // Sync URL whenever selections change (replace = no extra history entries)
  useEffect(() => {
    setSearchParams(
      { eq: equipment, op: operation, mi: String(miles) },
      { replace: true }
    );
  }, [equipment, operation, miles, setSearchParams]);

  // Auto-scroll to calculator when the URL arrives with calc params
  const didScroll = useRef(false);
  useEffect(() => {
    if (didScroll.current) return;
    if (searchParams.get("eq") && sectionRef.current) {
      didScroll.current = true;
      setTimeout(() => {
        sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyLink = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        toast.success("Calculator link copied!");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => toast.error("Copy failed — use the address bar."));
  };

  const eq = EQUIPMENT.find((e) => e.id === equipment);
  const op = OPERATIONS.find((o) => o.id === operation);
  // Pick the right rate depending on operation type
  const rpm = operation === "owner-op" ? eq.ooRPM : eq.leaseRPM;
  const result = op.calc(miles, rpm, eq.companyCPM);
  const netPositive = result.net > 0;

  // Dynamic sub-text for company operation based on selected equipment
  const getOpSub = (o) => {
    if (o.id === "company") return `${(eq.companyCPM * 100).toFixed(0)}¢ CPM`;
    if (o.id === "lease")   return `88% · $${eq.leaseRPM.toFixed(2)}/mi avg`;
    if (o.id === "owner-op") return `$${eq.ooRPM.toFixed(2)}/mi full rate`;
    return o.sub;
  };

  return (
    <section ref={sectionRef} className="bg-[#0A0A0A] py-24 md:py-32" data-testid="pay-calculator">
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-12">
          <div>
            <span className="font-head text-xs md:text-sm font-bold uppercase tracking-[0.4em] text-primary">
              Gross Pay Calculator
            </span>
            <h2 className="font-head text-3xl md:text-5xl font-medium uppercase tracking-[0.15em] mt-4 text-white">
              Know Your Earnings
            </h2>
            <p className="mt-4 text-white/60 max-w-xl">
              Select your setup and target miles. See exactly what you'd gross, what expenses look like, and what hits your pocket.
            </p>
          </div>

          {/* Share button */}
          <button
            type="button"
            data-testid="calc-share-btn"
            onClick={copyLink}
            className={`self-start flex items-center gap-2 font-head text-xs uppercase tracking-[0.2em] px-5 py-3 border-2 transition-all duration-200 ${
              copied
                ? "border-primary text-primary bg-primary/10"
                : "border-white/20 text-white/60 hover:border-white/50 hover:text-white"
            }`}
          >
            {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
            {copied ? "Copied!" : "Share This Calc"}
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* Controls */}
          <div className="space-y-8">

            {/* Equipment */}
            <div>
              <p className="font-head text-xs uppercase tracking-[0.3em] text-white/50 mb-4">Equipment Type</p>
              <div className="grid grid-cols-3 gap-3">
                {EQUIPMENT.map((e) => {
                  const Icon = e.Icon;
                  return (
                    <button
                      key={e.id}
                      data-testid={`calc-eq-${e.id}`}
                      type="button"
                      onClick={() => setEquipment(e.id)}
                      className={`relative py-5 px-3 border-2 transition-all duration-200 flex flex-col items-center gap-2 ${
                        equipment === e.id
                          ? "border-primary bg-primary/10"
                          : "border-white/15 hover:border-white/40"
                      }`}
                    >
                      <Icon
                        className={`w-6 h-6 ${equipment === e.id ? "text-primary" : "text-white/50"}`}
                        strokeWidth={1.5}
                      />
                      <span
                        className={`font-head text-xs uppercase tracking-[0.15em] ${
                          equipment === e.id ? "text-primary" : "text-white/70"
                        }`}
                      >
                        {e.label}
                      </span>
                      {equipment === e.id && (
                        <motion.div
                          layoutId="eq-indicator"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Operation */}
            <div>
              <p className="font-head text-xs uppercase tracking-[0.3em] text-white/50 mb-4">Operation Type</p>
              <div className="grid grid-cols-3 gap-3">
                {OPERATIONS.map((o) => (
                  <button
                    key={o.id}
                    data-testid={`calc-op-${o.id}`}
                    type="button"
                    onClick={() => setOperation(o.id)}
                    className={`relative py-4 px-3 border-2 transition-all duration-200 flex flex-col items-start gap-1 ${
                      operation === o.id
                        ? "border-primary bg-primary/10"
                        : "border-white/15 hover:border-white/40"
                    }`}
                  >
                    <span
                      className={`font-head text-xs uppercase tracking-[0.12em] leading-snug ${
                        operation === o.id ? "text-primary" : "text-white/80"
                      }`}
                    >
                      {o.label}
                    </span>
                    <span
                      className={`font-head text-[10px] uppercase tracking-[0.1em] ${
                        operation === o.id ? "text-primary/70" : "text-white/40"
                      }`}
                    >
                      {getOpSub(o)}
                    </span>
                    {operation === o.id && (
                      <motion.div
                        layoutId="op-indicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Miles Slider */}
            <div>
              <div className="flex items-baseline justify-between mb-4">
                <p className="font-head text-xs uppercase tracking-[0.3em] text-white/50">Target Miles / Week</p>
                <span className="font-head text-3xl font-bold text-primary">
                  {miles.toLocaleString()}
                  <span className="text-sm text-white/50 ml-1">mi</span>
                </span>
              </div>
              <input
                type="range"
                data-testid="calc-miles-slider"
                min={500}
                max={5000}
                step={100}
                value={miles}
                onChange={(e) => setMiles(Number(e.target.value))}
                className="w-full h-1.5 rounded-none appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #FFB300 0%, #FFB300 ${
                    ((miles - 500) / 4500) * 100
                  }%, rgba(255,255,255,0.1) ${((miles - 500) / 4500) * 100}%, rgba(255,255,255,0.1) 100%)`,
                }}
              />
              <div className="flex justify-between mt-1">
                <span className="font-head text-[10px] text-white/30 uppercase tracking-[0.2em]">500</span>
                <span className="font-head text-[10px] text-white/30 uppercase tracking-[0.2em]">5,000</span>
              </div>
            </div>
          </div>

          {/* Results */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`${equipment}-${operation}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="border border-white/15 bg-white/[0.03] p-8 space-y-6"
            >
              {/* Weekly Gross */}
              <div className="border-b border-white/10 pb-6">
                <p className="font-head text-xs uppercase tracking-[0.3em] text-white/50 mb-2">Weekly Gross</p>
                <div className="font-head text-5xl md:text-6xl font-bold text-primary">
                  <AnimNum value={result.gross} prefix="$" />
                </div>
                <p className="text-white/40 text-sm mt-1">{result.note}</p>
              </div>

              {/* Expenses */}
              {result.expenses.filter((ex) => ex.amount > 0).length > 0 && (
                <div className="border-b border-white/10 pb-6">
                  <p className="font-head text-xs uppercase tracking-[0.3em] text-white/50 mb-4">Estimated Expenses</p>
                  <div className="space-y-3">
                    {result.expenses
                      .filter((ex) => ex.amount > 0)
                      .map((ex) => (
                        <div key={ex.label} className="flex items-center justify-between">
                          <span className="text-white/60 text-sm">{ex.label}</span>
                          <span className="font-head text-white/80 text-sm">
                            -<AnimNum value={ex.amount} prefix="$" />
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Net Take-Home */}
              <div className="border-b border-white/10 pb-6">
                <p className="font-head text-xs uppercase tracking-[0.3em] text-white/50 mb-2">Est. Net Take-Home</p>
                <div
                  className={`font-head text-4xl md:text-5xl font-bold ${
                    netPositive ? "text-green-400" : "text-red-400"
                  }`}
                >
                  <AnimNum value={Math.max(result.net, 0)} prefix="$" />
                </div>
                <p className="text-white/40 text-xs mt-1">Before personal income taxes</p>
              </div>

              {/* Settlement */}
              <div>
                <p className="font-head text-xs uppercase tracking-[0.3em] text-white/50 mb-2">Settlement</p>
                <p className="font-head text-sm uppercase tracking-[0.15em] text-white">{result.settlement}</p>
              </div>

              <Link
                to="/apply"
                data-testid="calc-apply-btn"
                className="flex items-center justify-between w-full font-head text-sm uppercase tracking-[0.2em] bg-primary text-black px-6 py-4 hover:bg-white transition-colors duration-300 group"
              >
                Apply For This Lane
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>

              <p className="text-white/25 text-[11px] leading-relaxed">
                * Estimates based on actual Hylas pay packages. Lease/Owner-Op assumes $3.40/mi flatbed · $3.20/mi reefer · $3.00/mi dry van avg gross. Fuel at $6/gal, 7 mpg avg. Truck note $600–800/wk; escrow $200 × 10 wks only. Actual earnings vary.
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
